import React, { Fragment, useState } from "react";
import { Modal, Row, Col, Spinner } from "react-bootstrap";
import styled from "styled-components";
import {
  CustomControl,
  CustomButton,
  CustomSelect,
  CustomInput,
  FormikFieldError,
} from "components/CustomForm";
import { MdClose } from "react-icons/md";
import { ErrorMessage, Field, Form, Formik } from "formik";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import {
  createCoroporation,
  updateCoroporation,
} from "network/apis/CorporationApi";
import {
  createUnion,
  getProvinces,
  getProvinceCities,
  updateUnion,
} from "network/apis/InformationApi";
import { isBlank, getErrorMessage } from "helpers/strings";

const ModalHolder = styled(Modal)`
  font-family: "YEKAN-BAKH-MEDIUM";
`;

const FormHolder = styled(Form)`
  direction: rtl;
`;

const Header = styled.div`
  font-size: 18pt;
  width: 100%;
  text-align: center;
  margin-top: 0.5rem;
  margin-bottom: 1.5rem;
`;

const CloseButton = styled.div`
  background-color: #424250;
  width: 2rem;
  height: 2rem;
  border-radius: 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  position: absolute;
  top: 12px;
  right: 10px;
`;

const AddCoroporationModal = ({ isOpen, onClose, onCreateSuccess, data, ...rest }) => {
  const provinceId = localStorage.getItem('province_id');

  const isEditMode = () => data && data != null;
  const getDefaultCity = () =>
    isEditMode()
      ? {
        label: data.city.name,
        value: data.city.city_id,
      }
      : null;

  const [loading, setLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState(
    getDefaultCity()
  );

  const { data: provincesData } = useQuery(
    ["get-province-cities"],
    () => getProvinceCities({id: provinceId}),
    {
      refetchOnWindowFocus: false,
    }
  );

  const corporationsMutation = useMutation(isEditMode() ? updateCoroporation : createCoroporation, {
    onSuccess: (res) => {
      if (res && res.data) {
        toast(`با موفقیت ${isEditMode() ? "ویرایش" : "ایجاد"} گردید`, {
          type: "success",
          position: "bottom-right",
        });
        onCreateSuccess?.();
        onClose();
      }
    },
    onError: (err) => {
      toast(getErrorMessage(err), {
        type: "error",
        position: "bottom-right",
      });
    },
    onSettled: () => {
      setLoading(false);
    },
  });

  const getProvinceCityOptions = () =>
    provincesData?.data
      ? provincesData.data.map((city, index) => {
        return { value: city?.city_id, label: city?.name };
      })
      : [];

  return (
    <ModalHolder
      show={isOpen}
      size={"md"}
      centered
      animation={false}
      onHide={onClose}
      {...rest}
    >
      <Modal.Body className="px-5">
        <CloseButton onClick={onClose}>
          <MdClose color="#fff" />
        </CloseButton>
        <Header>
          <span>{isEditMode() ? "ویرایش" : "افزودن"} شرکت تعاونی</span>
        </Header>
        <Formik
          initialValues={{
            name: data?.name,
            ceo_name: data?.ceo_name,
            national_id: data?.national_id,
            address: data?.address,
            ceo_phone_number: data?.ceo_phone_number,
            telephone: data?.telephone,
            postal_code: data?.postal_code,
          }}
          validate={(values) => {
            const errors = {};
            if (isBlank(values.name)) {
              errors.name = "ضروری";
            } else if (!selectedCity) {
              errors.city_id = "ضروری";
            } else if (isBlank(values.national_id)) {
              errors.national_id = "ضروری";
            }
            else if (isBlank(`${values.telephone}`) || `${values.telephone}`.length > 13) {
              errors.telephone = "شماره تلفن وارد شده صحیح نمی باشد";
            }
            else if (isBlank(values.ceo_name)) {
              errors.ceo_name = "ضروری";
            }
            else if (isBlank(`${values.ceo_phone_number}`) || `${values.ceo_phone_number}`.length > 13) {
              errors.ceo_phone_number = "شماره همراه وارد شده صحیح نمی باشد";
            }
            else if (isBlank(values.address)) {
              errors.address = "ضروری";
            }
            else if (isBlank(`${values.postal_code}`) || `${values.postal_code}`.length !== 10) {
              errors.postal_code = "کد پستی وارد شده صحیح نمی باشد";
            }
            return errors;
          }}
          onSubmit={(values, { setSubmitting }) => {
            setLoading(true);

            corporationsMutation.mutate({
              id: data?.coop_id,
              city_id: selectedCity?.value,
              ...values,
            });
          }}
        >
          {({ submitForm }) => (
            <FormHolder>
              <Fragment>
                <div className="d-flex justify-content-between mb-2 w-100">
                  <Col md={6} className="ps-1">
                    <Field type="text" name="name">
                      {({ field, meta: { touched, error } }) => (
                        <CustomControl
                          className={touched && error ? "invalid" : ""}
                          error={!isBlank(error)}
                          {...field}
                          placeholder="نام ثبتی شرکت تعاونی"
                          radius="14"
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="name"
                      component={FormikFieldError}
                    />
                  </Col>
                  <Col md={6} className="pe-1">
                    <Field type="text" name="city_id">
                      {({ field, meta: { touched, error } }) => (
                        <CustomSelect
                          className={touched && error ? "invalid" : ""}
                          error={!isBlank(error)}
                          {...field}
                          placeholder="نام شهرستان"
                          radius="14"
                          options={getProvinceCityOptions()}
                          onChange={setSelectedCity}
                          value={selectedCity}
                          menuPosition='fixed'
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="city_id"
                      component={FormikFieldError}
                    />
                  </Col>
                </div>
                <div className="d-flex justify-content-between mb-2 w-100">
                  <Col md={6} className="ps-1">
                    <Field type="text" name="national_id">
                      {({ field, meta: { touched, error } }) => (
                        <CustomControl
                          className={touched && error ? "invalid" : ""}
                          error={!isBlank(error)}
                          {...field}
                          placeholder="شناسه ملی شرکت تعاونی"
                          radius="14"
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="national_id"
                      component={FormikFieldError}
                    />
                  </Col>
                  <Col md={6} className="pe-1">
                    <Field type="text" name="telephone">
                      {({ field, meta: { touched, error } }) => (
                        <CustomControl
                          type='number'
                          className={touched && error ? "invalid" : ""}
                          error={!isBlank(error)}
                          {...field}
                          placeholder="شماره ثابت شرکت تعاونی"
                          radius="14"
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="telephone"
                      component={FormikFieldError}
                    />
                  </Col>
                </div>
                <div className="d-flex justify-content-between mb-2 w-100">
                  <Col md={6} className="ps-1">
                    <Field type="text" name="ceo_name">
                      {({ field, meta: { touched, error } }) => (
                        <CustomControl
                          className={touched && error ? "invalid" : ""}
                          error={!isBlank(error)}
                          {...field}
                          placeholder="نام مدیر عامل"
                          radius="14"
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="ceo_name"
                      component={FormikFieldError}
                    />
                  </Col>
                  <Col md={6} className="pe-1">
                    <Field type="text" name="ceo_phone_number">
                      {({ field, meta: { touched, error } }) => (
                        <CustomControl
                          type='number'
                          className={touched && error ? "invalid" : ""}
                          error={!isBlank(error)}
                          {...field}
                          placeholder="شماره همراه مدیر عامل"
                          radius="14"
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="ceo_phone_number"
                      component={FormikFieldError}
                    />
                  </Col>
                </div>
                <div className="d-flex justify-content-between mb-2 w-100">
                  <Col md={6} className="ps-1">
                    <Field type="text" name="address">
                      {({ field, meta: { touched, error } }) => (
                        <CustomControl
                          className={touched && error ? "invalid" : ""}
                          error={!isBlank(error)}
                          {...field}
                          placeholder="آدرس"
                          radius="14"
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="address"
                      component={FormikFieldError}
                    />
                  </Col>
                  <Col md={6} className="pe-1">
                    <Field type="text" name="postal_code">
                      {({ field, meta: { touched, error } }) => (
                        <CustomControl
                          type='number'
                          className={touched && error ? "invalid" : ""}
                          error={!isBlank(error)}
                          {...field}
                          placeholder="کد پستی"
                          radius="14"
                        />
                      )}
                    </Field>
                    <ErrorMessage
                      name="postal_code"
                      component={FormikFieldError}
                    />
                  </Col>
                </div>
                <div className="d-flex justify-content-center mt-4 mb-3 w-100">
                  <CustomButton
                    className="ms-3 w-25"
                    color="#7300FF"
                    fontSize={16}
                    onClick={onClose}
                  >
                    انصراف
                  </CustomButton>
                  <CustomButton
                    fontSize={16}
                    onClick={submitForm}
                    disabled={loading}
                    className="w-25 d-flex align-items-center justify-content-center"
                  >
                    {loading ? (
                      <Spinner animation="border" role="status" size="sm" />
                    ) : (
                      <span>ثبت</span>
                    )}
                  </CustomButton>
                </div>
              </Fragment>
            </FormHolder>
          )}
        </Formik>
      </Modal.Body>
    </ModalHolder>
  );
};

export default AddCoroporationModal;
